/**
 * "Ask an admin to turn this on" (marketplace.md 4.5). Staff without
 * `marketplace.purchase` send a request with an optional reason, prefilled
 * with the office's own reason chip ("31 missed calls last week at SAG").
 */
import type { CatalogModule, StoreOffice } from '@velora/contracts/marketplace';
import { Button, Drawer, Field, Select, Textarea, toast } from '@velora/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { errorMessage, marketplaceApi, useMarketplaceRefresh } from '../api.js';
import { Kbd } from './bits.js';
import { ModuleIcon } from './ModuleIcon.js';

export interface RequestDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  module: CatalogModule;
  offices: readonly StoreOffice[];
  defaultOfficeId: string | null;
  suggestedReason?: string | null;
}

export function RequestDrawer(props: RequestDrawerProps) {
  const { module: m, offices } = props;
  const [office, setOffice] = useState<string>(props.defaultOfficeId ?? offices[0]?.id ?? '');
  const [reason, setReason] = useState('');
  const text = useRef<HTMLTextAreaElement>(null);
  const refresh = useMarketplaceRefresh();
  const send = useMutation({
    mutationFn: () =>
      marketplaceApi.createRequest({
        moduleKey: m.key,
        locationId: office,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: () => {
      void refresh();
      toast({
        title: 'Request sent',
        description: `An admin will see it in Marketplace requests. You will see their answer on ${m.name}.`,
        tone: 'success',
      });
      props.onOpenChange(false);
    },
    onError: (e) =>
      toast({ title: 'Request not sent', description: errorMessage(e), tone: 'danger' }),
  });

  useEffect(() => {
    if (!props.open) return;
    setOffice(props.defaultOfficeId ?? offices[0]?.id ?? '');
    setReason(props.suggestedReason ? `${props.suggestedReason}.` : '');
    setTimeout(() => text.current?.focus(), 40);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  return (
    <Drawer
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Request a module"
      description="Goes to the admins who can turn modules on"
      size="sm"
      footer={
        <div className="mk-foot">
          <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <span className="mk-grow" />
          <Button
            variant="primary"
            disabled={!office}
            loading={send.isPending}
            onClick={() => send.mutate()}
            data-testid="request-send"
          >
            Send request <Kbd>Ctrl</Kbd>
            <Kbd>Enter</Kbd>
          </Button>
        </div>
      }
    >
      <div
        className="mk-sheet"
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && office) {
            e.preventDefault();
            send.mutate();
          }
        }}
      >
        <div className="mk-sheet__mod">
          <ModuleIcon module={m} />
          <div>
            <div className="mk-sheet__name">{m.name}</div>
            <div className="mk-sheet__sub">{m.valueLine}</div>
          </div>
        </div>
        {offices.length > 1 ? (
          <Field label="For which office">
            <Select
              value={office}
              onValueChange={setOffice}
              options={offices.map((o) => ({ value: o.id, label: o.name }))}
            />
          </Field>
        ) : null}
        <Field label="Tell them why (optional)" hint="Up to 500 characters.">
          <Textarea
            ref={text}
            rows={5}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={`For example: what ${m.shortName} would save the front desk each week`}
          />
        </Field>
      </div>
    </Drawer>
  );
}
